import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const nextFrame = () => new Promise(resolve => requestAnimationFrame(() => resolve()));

/**
 * Export rendered `.pt-page` elements to a PDF (one DOM page => one PDF page).
 *
 * Notes:
 * - This preserves the on-screen design by rasterizing each page.
 * - Text will not be selectable/searchable (image-based PDF).
 * - Cross-origin images without CORS headers may not render.
 */
export const exportPtPagesToPdf = async ({
    filename = 'menu.pdf',
    pageSelector = '.pt-page',
    scale = 2,
} = {}) => {
    const pageElements = Array.from(document.querySelectorAll(pageSelector));

    if (pageElements.length === 0) {
        throw new Error(`No pages found for selector: ${pageSelector}`);
    }

    // Ensure fonts are loaded before rasterizing.
    if (document.fonts && document.fonts.ready) {
        try {
            await document.fonts.ready;
        } catch {
            // ignore
        }
    }

    // Temporarily ensure pages are visible (some templates hide non-active pages on screen).
    const classSnapshots = pageElements.map(el => ({
        el,
        hadHidden: el.classList.contains('hidden-on-screen'),
        hadActive: el.classList.contains('active'),
    }));

    classSnapshots.forEach(({ el }) => {
        el.classList.remove('hidden-on-screen');
        el.classList.add('active');
    });

    await nextFrame();

    let pdf;

    try {
        for (let index = 0; index < pageElements.length; index++) {
            const pageEl = pageElements[index];

            // html2canvas works best when the node is actually in the DOM.
            const canvas = await html2canvas(pageEl, {
                scale,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const pageWidth = canvas.width;
            const pageHeight = canvas.height;
            const orientation = pageWidth > pageHeight ? 'l' : 'p';

            if (!pdf) {
                pdf = new jsPDF({
                    orientation,
                    unit: 'px',
                    format: [pageWidth, pageHeight],
                    hotfixes: ['px_scaling'],
                    compress: true,
                });
            } else {
                pdf.addPage([pageWidth, pageHeight], orientation);
            }

            pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        }

        pdf.save(filename);
    } finally {
        // Restore original visibility classes.
        classSnapshots.forEach(({ el, hadHidden, hadActive }) => {
            el.classList.toggle('hidden-on-screen', hadHidden);
            el.classList.toggle('active', hadActive);
        });
    }
};

export default exportPtPagesToPdf;
